import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { employeeAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Loader2, Copy, Check, Mail, Sun, Moon, Maximize2, Minimize2, 
  ExternalLink, Shield
} from 'lucide-react';

const EmailSignature = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Customization options
  const [theme, setTheme] = useState('light'); // light or dark
  const [layout, setLayout] = useState('full'); // full or compact
  const [showVerifiedBadge, setShowVerifiedBadge] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await employeeAPI.getProfile();
      setProfile(res.data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const generateSignatureHTML = () => {
    const name = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'Your Name';
    const title = profile?.job_title || 'Professional';
    const vettId = user?.vett_id || 'VETT-XXXXXX';
    const profileUrl = `https://vett.com/p/${vettId}`;
    
    const colors = theme === 'light' 
      ? { bg: '#ffffff', text: '#1a1d26', textMuted: '#6b7080', accent: '#2563EB', border: '#e8ebf0' }
      : { bg: '#f8f9fb', text: '#1a1d26', textMuted: '#6b7080', accent: '#2563EB', border: '#e1e4e8' };

    if (layout === 'compact') {
      return `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif;">
  <tr>
    <td style="padding-right: 12px; vertical-align: middle;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="background-color: ${colors.accent}; width: 36px; height: 36px; border-radius: 8px; text-align: center; vertical-align: middle;">
            <span style="color: #ffffff; font-size: 16px; font-weight: bold;">V</span>
          </td>
        </tr>
      </table>
    </td>
    <td style="vertical-align: middle;">
      <span style="color: ${colors.text}; font-size: 14px; font-weight: 600;">${name}</span>
      <span style="color: ${colors.textMuted}; font-size: 13px;"> · ${title}</span>
      ${showVerifiedBadge ? `<br><a href="${profileUrl}" style="color: ${colors.accent}; font-size: 12px; text-decoration: none;">${vettId} ✓ Verified on VETT</a>` : ''}
    </td>
  </tr>
</table>
      `.trim();
    }

    // Full layout
    return `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif; max-width: 400px;">
  <tr>
    <td style="padding-bottom: 12px; border-bottom: 1px solid ${colors.border};">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="padding-right: 16px; vertical-align: top;">
            <table cellpadding="0" cellspacing="0" border="0">
              <tr>
                <td style="background-color: ${colors.accent}; width: 48px; height: 48px; border-radius: 10px; text-align: center; vertical-align: middle;">
                  <span style="color: #ffffff; font-size: 20px; font-weight: bold;">V</span>
                </td>
              </tr>
            </table>
          </td>
          <td style="vertical-align: top;">
            <p style="margin: 0 0 4px 0; color: ${colors.text}; font-size: 16px; font-weight: 600;">${name}</p>
            <p style="margin: 0 0 8px 0; color: ${colors.textMuted}; font-size: 14px;">${title}</p>
            ${showVerifiedBadge ? `
            <table cellpadding="0" cellspacing="0" border="0">
              <tr>
                <td style="background-color: ${theme === 'light' ? '#f0f9f0' : '#1a2e1a'}; padding: 4px 10px; border-radius: 4px;">
                  <a href="${profileUrl}" style="color: ${colors.accent}; font-size: 12px; text-decoration: none; font-weight: 500;">
                    ${vettId} · Verified on VETT ↗
                  </a>
                </td>
              </tr>
            </table>
            ` : ''}
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding-top: 10px;">
      <a href="https://getvett.com" style="color: ${colors.textMuted}; font-size: 11px; text-decoration: none;">
        Verified work history powered by VETT
      </a>
    </td>
  </tr>
</table>
    `.trim();
  };

  const handleCopy = async () => {
    try {
      const html = generateSignatureHTML();
      
      // Try to copy as rich text (HTML)
      if (navigator.clipboard && window.ClipboardItem) {
        const blob = new Blob([html], { type: 'text/html' });
        const clipboardItem = new ClipboardItem({ 'text/html': blob });
        await navigator.clipboard.write([clipboardItem]);
      } else {
        // Fallback to plain text copy
        await navigator.clipboard.writeText(html);
      }
      
      setCopied(true);
      toast.success('Signature copied! Paste it into your email client settings.');
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      // Fallback: select and copy
      const textArea = document.createElement('textarea');
      textArea.value = generateSignatureHTML();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success('Signature copied!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  const name = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'Your Name';
  const title = profile?.job_title || 'Professional';
  const vettId = user?.vett_id || 'VETT-XXXXXX';

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-6" data-testid="email-signature-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">Email Signature</h1>
          <p className="mt-1 text-[#5B6472] text-sm">
            Add your verified VETT credentials to your email signature.
          </p>
        </div>

        {/* Preview Card */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#0A0F1E] flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#1B5AFF]" />
              Preview
            </h2>
            <Button
              onClick={handleCopy}
              className={`rounded-xl transition-all ${
                copied 
                  ? 'bg-green-500 hover:bg-green-600' 
                  : 'bg-[#1B5AFF] hover:bg-[#1B5AFF]/90'
              } text-[#0A0F1E]`}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Signature
                </>
              )}
            </Button>
          </div>

          {/* Live Preview */}
          <div 
            className={`rounded-xl p-6 border ${
              theme === 'light' 
                ? 'bg-white border-gray-200' 
                : 'bg-[#1a1a2e] border-[#E8EDF5]'
            }`}
          >
            {layout === 'compact' ? (
              // Compact Layout Preview
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1B5AFF] flex items-center justify-center flex-shrink-0">
                  <span className="text-[#0A0F1E] font-bold">V</span>
                </div>
                <div>
                  <span className={`font-semibold text-sm ${theme === 'light' ? 'text-gray-900' : 'text-[#0A0F1E]'}`}>
                    {name}
                  </span>
                  <span className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
                    {' '}· {title}
                  </span>
                  {showVerifiedBadge && (
                    <div>
                      <a href="#" className="text-[#1B5AFF] text-xs hover:underline">
                        {vettId} ✓ Verified on VETT
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Full Layout Preview
              <div>
                <div className={`pb-3 border-b ${theme === 'light' ? 'border-gray-200' : 'border-[#E8EDF5]'}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#1B5AFF] flex items-center justify-center flex-shrink-0">
                      <span className="text-[#0A0F1E] font-bold text-xl">V</span>
                    </div>
                    <div>
                      <p className={`font-semibold ${theme === 'light' ? 'text-gray-900' : 'text-[#0A0F1E]'}`}>
                        {name}
                      </p>
                      <p className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
                        {title}
                      </p>
                      {showVerifiedBadge && (
                        <div className={`inline-block mt-2 px-2.5 py-1 rounded text-xs ${
                          theme === 'light' ? 'bg-green-50' : 'bg-green-900/20'
                        }`}>
                          <a href="#" className="text-[#1B5AFF] font-medium hover:underline">
                            {vettId} · Verified on VETT ↗
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <p className={`text-[11px] mt-2.5 ${theme === 'light' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Verified work history powered by VETT
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Customization Options */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#0A0F1E] mb-6">Customize</h2>
          
          <div className="space-y-6">
            {/* Theme Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {theme === 'light' ? (
                  <Sun className="w-5 h-5 text-amber-400" />
                ) : (
                  <Moon className="w-5 h-5 text-blue-400" />
                )}
                <div>
                  <Label className="text-[#0A0F1E]">Theme</Label>
                  <p className="text-[#5B6472] text-xs">Match your email client's theme</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={theme === 'light' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTheme('light')}
                  className={theme === 'light' 
                    ? 'bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]' 
                    : 'border-[#E8EDF5] text-[#5B6472]'
                  }
                >
                  Light
                </Button>
                <Button
                  variant={theme === 'dark' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTheme('dark')}
                  className={theme === 'dark' 
                    ? 'bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]' 
                    : 'border-[#E8EDF5] text-[#5B6472]'
                  }
                >
                  Dark
                </Button>
              </div>
            </div>

            {/* Layout Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {layout === 'full' ? (
                  <Maximize2 className="w-5 h-5 text-[#1B5AFF]" />
                ) : (
                  <Minimize2 className="w-5 h-5 text-[#1B5AFF]" />
                )}
                <div>
                  <Label className="text-[#0A0F1E]">Layout</Label>
                  <p className="text-[#5B6472] text-xs">Choose signature size</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={layout === 'compact' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setLayout('compact')}
                  className={layout === 'compact' 
                    ? 'bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]' 
                    : 'border-[#E8EDF5] text-[#5B6472]'
                  }
                >
                  Compact
                </Button>
                <Button
                  variant={layout === 'full' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setLayout('full')}
                  className={layout === 'full' 
                    ? 'bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]' 
                    : 'border-[#E8EDF5] text-[#5B6472]'
                  }
                >
                  Full
                </Button>
              </div>
            </div>

            {/* Verified Badge Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-green-400" />
                <div>
                  <Label className="text-[#0A0F1E]">Verified Badge</Label>
                  <p className="text-[#5B6472] text-xs">Show your VETT ID and verification</p>
                </div>
              </div>
              <Switch
                checked={showVerifiedBadge}
                onCheckedChange={setShowVerifiedBadge}
              />
            </div>
          </div>
        </Card>

        {/* Instructions */}
        <Card className="bg-[#F5F8FF]/30 border border-[#E8EDF5] rounded-xl p-6">
          <h3 className="text-[#0A0F1E] font-medium mb-4">How to add to your email</h3>
          <div className="space-y-3 text-[#5B6472] text-sm">
            <div className="flex gap-3">
              <span className="text-[#1B5AFF] font-bold">1.</span>
              <p>Click "Copy Signature" above</p>
            </div>
            <div className="flex gap-3">
              <span className="text-[#1B5AFF] font-bold">2.</span>
              <p><strong className="text-[#0A0F1E]">Gmail:</strong> Settings → See all settings → Signature → Paste</p>
            </div>
            <div className="flex gap-3">
              <span className="text-[#1B5AFF] font-bold">3.</span>
              <p><strong className="text-[#0A0F1E]">Outlook:</strong> File → Options → Mail → Signatures → Paste</p>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default EmailSignature;
